import { useEffect } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  techdocsPlugin,
  TechDocsReaderPageContent,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { UserSettingsPage, SettingsLayout } from '@backstage/plugin-user-settings';
import { UserNotificationSettingsCard } from '@backstage/plugin-notifications';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { CatalogHomePage } from './components/catalog/CatalogHomePage';
import { ArtifactRepositoryPage } from './components/catalog/ArtifactRepositoryPage';
import { searchPage } from './components/search/SearchPage';
import { CoIdePage } from './components/coide/CoIdePage';
import { UseCasesPage } from './components/catalog/usecases/UseCasesPage';
import { UseCaseDetailPage } from './components/catalog/usecases/UseCaseDetailPage';
import { PersistenceRepositoryPage } from './components/persistence/PersistenceRepositoryPage';
import { DocsInstancePage } from './components/catalog/DocsInstancePage';
import { DocsInstanceReaderPage } from './components/catalog/DocsInstanceReaderPage';
import { AiTrainerPage } from './components/aitrainer';
import { MatlabPage } from './components/matlab';
import { AuditLogPage } from './components/auditlog/AuditLogPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  AutoLogout,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import {
  gitlabAuthApiRef,
} from '@backstage/core-plugin-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';

const isLocalAuthBypass = process.env.NODE_ENV !== 'production';

const DocsLandingPage = () => <DocsInstancePage />;

/**
 * Proxy routes use an httpOnly infrastructure-session cookie because normal
 * browser navigation cannot attach a Backstage Bearer token.  The built-in
 * user-settings plugin signs out through `identityApi` only, so clear that
 * separate cookie before its sign-out click handler runs.
 */
const InfraSessionLogoutListener = () => {
  useEffect(() => {
    const revokeSession = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('[data-testid="sign-out"]')) return;

      void fetch('/api/infra/revoke-session', {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
      }).then(response => {
        if (!response.ok) {
          console.warn('Could not revoke infrastructure session during sign-out');
        }
      }).catch(() => {
        console.warn('Could not revoke infrastructure session during sign-out');
      });
    };

    document.addEventListener('click', revokeSession, true);
    return () => document.removeEventListener('click', revokeSession, true);
  }, []);

  return null;
};

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto={isLocalAuthBypass}
        providers={
          isLocalAuthBypass
            ? ['guest']
            : [
                {
                  id: 'gitlab',
                  title: 'GitLab Login',
                  message: 'Sign in with gitlab.tu-ilmenau.de',
                  apiRef: gitlabAuthApiRef,
                },
              ]
        }
      />
    ),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog" element={<CatalogHomePage />} />
    <Route path="/artifacts" element={<ArtifactRepositoryPage />} />
    <Route path="/catalog/list" element={<CatalogIndexPage />} />
    <Route path="/catalog/use-cases" element={<UseCasesPage />} />
    <Route path="/catalog/use-cases/:id" element={<UseCaseDetailPage />} />
    <Route path="/ai-trainer" element={<AiTrainerPage />} />
    <Route path="/co-ide" element={<CoIdePage />} />
    <Route path="/matlab" element={<MatlabPage />} />
    <Route path="/persistence" element={<PersistenceRepositoryPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<DocsLandingPage />} />
    <Route path="/docs/instance/:id" element={<DocsInstanceReaderPage />} />
    <Route path="/docs-legacy/*" element={<Navigate to="/docs" replace />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={
        <TechDocsReaderPage>
          {({ entityMetadataValue, techdocsMetadataValue, entityRef }) => {
            const entityMeta = entityMetadataValue as any;
            const techdocsMeta = techdocsMetadataValue as any;
            const title = entityMeta?.title ?? entityRef.name;
            const owner = entityMeta?.owner ?? entityMeta?.spec?.owner ?? 'Unknown';
            const lifecycle = entityMeta?.lifecycle ?? entityMeta?.spec?.lifecycle ?? 'active';
            const description = entityMeta?.description ?? techdocsMeta?.description ?? '';

            return (
              <div style={{ display: 'grid', gap: 20 }}>
                <div style={{ borderBottom: '1px solid rgba(0,0,0,0.12)', padding: '20px 16px 10px' }}>
                  <div style={{ maxWidth: 1280, margin: '0 auto', width: '100%' }}>
                    <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
                      <button
                        type="button"
                        onClick={() => window.location.assign('/docs')}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: '#007eb1',
                          padding: 0,
                          cursor: 'pointer',
                          fontSize: 13,
                        }}
                      >
                        Documentation
                      </button>
                      {' / '}
                      <span>{title}</span>
                    </div>
                    <h1 style={{ margin: '0 0 6px', fontSize: 32, lineHeight: 1.3 }}>{title}</h1>
                    {description ? <p style={{ margin: 0, fontSize: 15, color: '#666' }}>{description}</p> : null}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '120px 1fr',
                        gap: '8px 12px',
                        marginTop: 16,
                        maxWidth: 680,
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Component</div>
                      <div style={{ fontSize: 14 }}>{title}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Owner</div>
                      <div style={{ fontSize: 14 }}>{owner}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Lifecycle</div>
                      <div style={{ fontSize: 14 }}>{lifecycle}</div>
                    </div>
                  </div>
                </div>
                <div style={{ maxWidth: 1280, margin: '0 auto', width: '100%', padding: '0 16px 24px' }}>
                  <TechDocsReaderPageContent />
                </div>
              </div>
            );
          }}
        </TechDocsReaderPage>
      }
    />
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      <SettingsLayout.Route path="/notifications" title="Notifications">
        <UserNotificationSettingsCard originNames={{}} />
      </SettingsLayout.Route>
    </Route>
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/audit-log" element={<AuditLogPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AutoLogout />
    <InfraSessionLogoutListener />
    <SignalsDisplay />
    <AppRouter>
        <Root>
          {routes}
        </Root>
    </AppRouter>
  </>,
);
